/* eslint-disable react/prop-types */
import React, {
  useCallback,
  useMemo,
  useEffect,
  useState,
  useContext,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
  Row,
  Select,
} from 'antd';
import {
  GroupOutlined,
  SettingOutlined,
} from '@ant-design/icons';

import FormWorkflowContext from './FormWorkflowContext';
import WorkflowActionNode from './WorkflowActionNode';
import WorkflowSimpleMappingConfigureDrawer from './WorkflowSimpleMappingConfigureDrawer';
import UpdateAddTimestampCheckbox from './UpdateAddTimestampCheckbox';
import WorkflowHandle from './WorkflowHandle';

import UserAssignmentSelector from './selectors/UserAssignmentSelector';
import BoardColorPicker from '../../boards/BoardColorPicker';
import { getBoardDetails } from '../../boards/state/boards.actions';
import BorderlessButton from '../../common/buttons/BorderlessButton';
import { getType, updateData } from './workflowHelpers';
import useToggle from '../../common/hooks/useToggle';
import { isFieldValid } from './update.constants';
import { getFieldMap, resolveFormDivisionIds } from '../formHelpers';
import OnTraccrCheckbox from '../../common/inputs/OnTraccrCheckbox';

const { Option } = Select;

const HOVER_TEXT = 'This step can update a board card based on the fields submitted.';
const DISABLED_TEXT = 'Add a Card Choose One or More field to enable this step';
const AUTHOR_HELP = 'The card will be assigned to the form\'s author';

const ERROR_DETAILS = {
  content: (
    <div style={{ maxWidth: 300 }}>
      This step requires the <b>Link Card Field</b> to be configured and at least one of the other
      fields.
    </div>
  ),
};

export default function WorkflowCardUpdateNode({
  isDisplay,
  setElements,
  setDataMap,
  divisionId,
  divisionIds = [],
  name,
  sections = [],
  isExternalForm,
  setDisableNext,
} = {}) {
  const relevantDivisionIds = resolveFormDivisionIds({ divisionIds, divisionId });

  return function WorkflowCardUpdateNodeComponent({
    id,
    draggable,
    data = {},
    disabled,
  }) {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { isToggled, toggle } = useToggle();
    const { dataMap = {} } = useContext(FormWorkflowContext) ?? {};

    // Merge data with dataMap so drawer receives latest state
    const nodeData = useMemo(() => ({
      ...data,
      ...(dataMap[id] || {}),
    }), [data, dataMap, id]);

    const {
      fieldMappings: initialFieldMappings = {},
      sectionMappings: initialSectionMappings = {},
      boardId: initialBoardId,
      cardFieldId: initialCardFieldId,
      status: initialStatus,
      color: initialCardColor,
      shouldOverride: initialShouldOverride = true,
      addTimestamps,
      users: initialUsers = [],
      cardTitleFieldId: initialCardTitleFieldId,
      timestampConfig: initialTimestampConfig,
    } = nodeData;

    const title = `Update Board Card${disabled ? ' - DISABLED' : ''}`;

    const boards = useSelector((state) => state.boards.boards);
    const boardDetailsMap = useSelector((state) => state.boards.boardDetailsMap);
    const cardTemplates = useSelector((state) => state.boards.cardTemplates);
    const customTables = useSelector((state) => state.forms.customTables);
    const boardStatuses = useSelector((state) => state.boards.boardStatuses);

    const [selectedCardColor, setSelectedCardColor] = useState(initialCardColor);
    const [selectedBoardId, setSelectedBoardId] = useState(initialBoardId);
    const [selectedCardFieldId, setSelectedCardFieldId] = useState(initialCardFieldId);
    const [selectedFieldMappings, setSelectedFieldMappings] = useState(initialFieldMappings);
    const [selectedSectionMappings, setSelectedSectionMappings] = useState(initialSectionMappings);
    const [selectedStatus, setSelectedStatus] = useState(initialStatus);
    const [override, setOverride] = useState(initialShouldOverride);
    const [selectedUsers, setSelectedUsers] = useState(initialUsers);
    const [selectedCardTitleId, setSelectedCardTitleId] = useState(initialCardTitleFieldId);
    const [selectedAddTimestamps, setSelectedAddTimestamps] = useState(addTimestamps);
    const [selectedTimestampConfig, setSelectedTimestampConfig] = useState(
      initialTimestampConfig || {
        source: 'stepExecution',
        includeTime: false,
        includeInitials: false,
      },
    );

    const boardStatusMap = useMemo(() => (
      boardStatuses?.reduce((acc, status) => {
        if (!acc[status.boardId]) acc[status.boardId] = [];
        acc[status.boardId].push(status);
        return acc;
      }, {}) ?? {}
    ), [boardStatuses]);

    const disableNext = useMemo(() => {
      const isCardFieldSet = selectedCardFieldId && selectedBoardId;

      const hasOtherConfigs = selectedStatus
        || (selectedFieldMappings && Object.keys(selectedFieldMappings).length > 0)
        || (selectedSectionMappings && Object.keys(selectedSectionMappings).length > 0)
        || selectedCardColor
        || (Array.isArray(selectedUsers) && selectedUsers.length > 0)
        || selectedCardTitleId;

      return !isCardFieldSet || !hasOtherConfigs;
    }, [
      selectedCardFieldId,
      selectedBoardId,
      selectedStatus,
      selectedFieldMappings,
      selectedSectionMappings,
      selectedCardColor,
      selectedUsers,
      selectedCardTitleId,
    ]);

    useEffect(() => {
      if (!setDisableNext) return;

      setDisableNext((prev) => ({ ...prev, [id]: disableNext }));
    }, [disableNext, setDisableNext, id]);

    const setMap = {
      color: setSelectedCardColor,
      boardId: setSelectedBoardId,
      cardFieldId: setSelectedCardFieldId,
      fieldMappings: setSelectedFieldMappings,
      sectionMappings: setSelectedSectionMappings,
      status: setSelectedStatus,
      cardTitleFieldId: setSelectedCardTitleId,
    };
    const onDataChange = useCallback((key) => (newValue) => {
      if (!setDataMap || !id) return;
      setMap[key]?.(newValue);
      setDataMap(updateData(id, { [key]: newValue }));
      if (key === 'shouldOverride') {
        setOverride(newValue);
      }
    }, [id, setDataMap]);
    const onCheckChanged = useCallback((key) => (checked) => {
      onDataChange(key)(checked);
    }, [onDataChange]);

    const onTimestampChanged = useCallback((newValue) => {
      setSelectedAddTimestamps(newValue);
      setDataMap(updateData(id, { addTimestamps: newValue }));
    }, [setDataMap, id]);

    const onTimestampConfigChanged = useCallback((newConfig) => {
      setSelectedTimestampConfig(newConfig);
      setDataMap(updateData(id, { timestampConfig: newConfig }));
    }, [setDataMap, id]);

    const onUsersChange = useCallback((newSelectedUsers) => {
      setDataMap((dataMap) => ({
        ...dataMap,
        [id]: { ...dataMap[id], users: newSelectedUsers },
      }));
      setSelectedUsers(newSelectedUsers);
    }, [id]);

    const boardColors = useMemo(() => (
      boardDetailsMap[selectedBoardId]?.colors
    ), [boardDetailsMap, selectedBoardId]);

    const cardTypeId = useMemo(() => boards[selectedBoardId]?.cardTypeId, [
      boards,
      selectedBoardId,
    ]);
    const cardSections = useMemo(() => cardTemplates?.[cardTypeId]?.fields || [], [
      cardTemplates,
      cardTypeId,
    ]);
    const {
      flattenedCardFields,
    } = useMemo(() => {
      const fields = [];
      cardSections.forEach((section) => {
        const { fields: sectionFields = [], name: sectionName } = section;
        sectionFields.forEach((field) => {
          const {
            configProps: {
              title: fieldTitle,
              dataType: fieldDataType,
              subDataType,
              subType,
            } = {},
            selectedType: fieldType,
            id: fieldId,
          } = field;

          if (isFieldValid(field, customTables)) {
            fields.push({
              id: fieldId,
              key: fieldId,
              text: `${sectionName} - ${fieldTitle}`,
              type: fieldType,
              dataType: fieldDataType,
              subDataType: subDataType ?? subType,
            });
          }
        });
      });
      return {
        flattenedCardFields: fields,
      };
    }, [cardSections, customTables]);

    const { cardFieldOptions = [], textFieldOptions = [] } = useMemo(() => {
      const cardFieldOpts = [
        { label: 'Card(s) Linked to Form', value: 'formCards' },
      ];
      const textFieldOpts = [];
      sections.forEach(({ fields = [] }) => {
        fields.forEach((field) => {
          const type = getType(field);
          const {
            configProps: { title: fieldTitle } = {},
            id: fieldId,
          } = field;
          if (type === 'dropdown - Cards') {
            cardFieldOpts.push({ label: fieldTitle, value: fieldId });
          } else if (type === 'text' || field.selectedType === 'dropdown') {
            textFieldOpts.push({ label: fieldTitle, value: fieldId });
          }
        });
      });
      return { cardFieldOptions: cardFieldOpts, textFieldOptions: textFieldOpts };
    }, [sections]);

    const fieldMap = useMemo(() => getFieldMap(sections, 'id'), [sections]);
    const cardFieldBoard = useMemo(() => {
      const {
        configProps: {
          subDataType,
        } = {},
      } = fieldMap[selectedCardFieldId] || {};
      return subDataType;
    }, [fieldMap, selectedCardFieldId]);

    const boardOptions = useMemo(() => {
      const options = Object.keys(boards).map((boardId) => (
        <Option key={boardId} value={boardId} label={boards[boardId].title}>
          {boards[boardId].title}
        </Option>
      ));
      return options;
    }, [boards]);

    const statusOptions = useMemo(() => {
      const {
        [selectedBoardId]: statuses,
      } = boardStatusMap;
      if (!statuses) return [];
      const options = statuses.map?.((status) => (
        <Option key={status.id} value={status.id} label={status.title}>
          {status.title}
        </Option>
      )) ?? [];
      return options;
    }, [boardStatusMap, selectedBoardId]);

    useEffect(() => {
      if (cardFieldBoard) {
        onDataChange('boardId')(cardFieldBoard);
      }
    }, [cardFieldBoard]);

    useEffect(() => {
      if (!selectedBoardId || selectedBoardId in boardDetailsMap) return;
      dispatch(getBoardDetails(selectedBoardId, false));
    }, [selectedBoardId, boardDetailsMap]);

    useEffect(() => {
      const {
        [selectedBoardId]: statuses,
      } = boardStatusMap;
      if (statuses?.length > 0 && !statuses?.some?.((status) => status.id === selectedStatus)) {
        onDataChange('status')(undefined);
      }
    }, [boardStatusMap, selectedBoardId, selectedStatus]);

    useEffect(() => {
      setSelectedAddTimestamps(addTimestamps);
    }, [addTimestamps]);

    const sourceSections = useMemo(() => (
      [{
        name: 'Form Data',
        fields: [{
          id: 'formNumber',
          configProps: {
            title: 'Form Number',
          },
          selectedType: 'text',
        }],
      }].concat(sections)
    ), [sections]);

    return (
      <WorkflowActionNode
        id={id}
        title={title}
        type="updateBoardCard"
        Icon={GroupOutlined}
        draggable={draggable}
        isDisplay={isDisplay || disabled}
        onNodeUpdate={(...args) => {
          setElements(...args);
          if (setDisableNext) {
            setDisableNext((prev) => ({ ...prev, [id]: false }));
          }
        }}
        hover={disabled ? DISABLED_TEXT : HOVER_TEXT}
        style={disabled ? { opacity: 0.7 } : {}}
        errorPopover={disableNext ? ERROR_DETAILS : null}
      >
        {!draggable
          && (
          <div>
            <Row style={{ marginTop: 10 }}>
              Link Card Field
            </Row>
            <Row style={{ margin: '10px 0px' }}>
              <Select
                onChange={onDataChange('cardFieldId')}
                value={selectedCardFieldId}
                showSearch
                optionFilterProp="label"
                options={cardFieldOptions}
              />
            </Row>
            <Row style={{ marginTop: 10 }}>
              Select
              {' '}
              {t('Board')}
              {' '}
            </Row>
            <Row style={{ margin: '10px 0px' }}>
              <Select
                onChange={onDataChange('boardId')}
                value={selectedBoardId}
                showSearch
                optionFilterProp="label"
                disabled={!selectedCardFieldId || cardFieldBoard}
              >
                {boardOptions}
              </Select>
            </Row>
            <Row style={{ marginTop: 10 }}>
              Select
              {' '}
              {t('Status')}
              {' '}
            </Row>
            <Row style={{ margin: '10px 0px' }}>
              <Select
                onChange={onDataChange('status')}
                value={selectedStatus}
                showSearch
                disabled={!selectedBoardId}
                allowClear
              >
                {statusOptions}
              </Select>
            </Row>
            <Row style={{ marginTop: 10 }}>
              Select
              {' '}
              {t('Card Color')}
              {' '}
            </Row>
            <BoardColorPicker
              isNotDisplay={!isDisplay}
              allowClear
              value={selectedCardColor}
              onChange={onDataChange('color')}
              boardColors={boardColors}
            />
            <UserAssignmentSelector
              text="Assign user(s):"
              type="user"
              isDisplay={isDisplay}
              draggable={draggable}
              divisions={relevantDivisionIds}
              onChange={onUsersChange}
              selected={selectedUsers}
              showFields
              sections={sections}
              showFormAuthor={!isExternalForm}
              formAuthorHelperText={AUTHOR_HELP}
            />
            <Row style={{ marginTop: 10 }}>
              Update Title
            </Row>
            <Row style={{ margin: '10px 0px' }}>
              <Select
                onChange={onDataChange('cardTitleFieldId')}
                value={selectedCardTitleId}
                showSearch
                allowClear
                optionFilterProp="label"
                options={textFieldOptions}
              />
            </Row>
            <Row style={{ margin: '20px 0px' }}>
              <BorderlessButton
                title="Configure"
                style={{ margin: 5 }}
                iconNode={<SettingOutlined />}
                onClick={toggle}
              />
            </Row>
            <OnTraccrCheckbox
              style={{ marginTop: 5 }}
              onChange={onCheckChanged('shouldOverride')}
              defaultChecked={initialShouldOverride}
              value={override}
              label="Override Card"
              hoverNode={(
                <div style={{ width: 300 }}>
                  If turned on, the card data will be overwritten with the new data.
                  <br />
                  <br />
                  If turned off, the card data will be appended to the existing data.
                  <br />
                  <br />
                  Note: Date/Time and Date/Time Range fields will always override existing data.
                </div>
              )}
            />
            {!override && (
              <UpdateAddTimestampCheckbox
                onChange={onTimestampChanged}
                initialChecked={addTimestamps}
                value={selectedAddTimestamps}
                sections={sections}
                onTimestampConfigChange={onTimestampConfigChanged}
                timestampConfig={selectedTimestampConfig}
                showInitialsCheckbox
              />
            )}
            <WorkflowSimpleMappingConfigureDrawer
              id={id}
              visible={isToggled}
              onClose={toggle}
              onSubmit={toggle}
              sourceName={name}
              sourceSections={sourceSections}
              fieldMappings={selectedFieldMappings}
              onFieldMappingsChange={onDataChange('fieldMappings')}
              sectionMappings={selectedSectionMappings}
              onSectionMappingsChange={onDataChange('sectionMappings')}
              targetSections={cardSections}
              shouldAllowDuplicateSectionMapping
              data={nodeData}
              setDataMap={setDataMap}
              fields={flattenedCardFields}
              updateExistingRowsOnly
              canExcludeEmptyQuantity
              canResetMaterialsAddedDate
            />
            <WorkflowHandle type="target" position="top" disabled={isDisplay} />
          </div>
          )}
      </WorkflowActionNode>
    );
  };
}
